// import dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';

import Tooltip from '~/common/components/Tooltip';
import InfoIcon from '~/common/components/InfoIcon2';
import formatCurrency from '~/common/utils/formatCurrency.js';
import AnnualPricingSwitch from '~/common/components/AnnualPricingSwitch';

import { isTouchDevice } from '~/common/utils/deviceDetection.js';
import {
    PLAN_TERM_MONTHLY, PLAN_TERM_ANNUAL,
    BASE_ANNUAL_DISCOUNT_PERCENTAGE
} from '~/common/utils/consts.js';
import { gtm, createAnnualPricingEvent } from '~/common/utils/tracking.js';

// import stylesheet
import styles from './order-summary.css';

const IS_TOUCH_DEVICE = isTouchDevice();

@inject(
    'billingInfoStore',
    'convertToPaidStore',
    'offerSuiteStore',
    'lockNumberStore',
    'transferNumberStore',
    'promoCodeStore',
    'loginStore',
    'formsStore'
)
@observer
export default class OrderSummary extends Component {
    // calculate subtotal amount
    calculateSubtotal() {
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;
        const { offerRecurringCharge, smsPerNumberCharge, isTermAnnual, selectedPlanTerm } = this.props.offerSuiteStore;

        // if sms enabled and requested then sms fee, or 0
        const smsCharge = smsPerNumberCharge || 0;
        // if term us annual, multiply it by 12
        const preCalculatedCost = isTermAnnual ? (offerRecurringCharge + smsCharge) * 12 : (offerRecurringCharge + smsCharge);
        // if taxes are calculated do the math off order total otherwise off offer price
        return calculatedTotal ? orderTotal[selectedPlanTerm].TotalEstimatedBeforeDiscount : preCalculatedCost;
    }

    // calculate total amount
    calculatedTotal() {
        const { promoCodeAmount, isCouponValid } = this.props.promoCodeStore;
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { selectedPlanTerm } = this.props.offerSuiteStore;

        const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;

        // grab subtotal
        const subtotal = this.calculateSubtotal();
        // promo code amount or 0
        const promoDiscount = isCouponValid && promoCodeAmount ? promoCodeAmount : 0;
        // if taxes are calculated do the math off order total otherwise offer price
        const total = calculatedTotal ? orderTotal[selectedPlanTerm].TotalAmount : subtotal - promoDiscount;
        // if total is less than 0, return 0
        return total <= 0 ? 0 : total;
    }

    calculateMonthlyTermPerYear() {
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;
        const { selectedOfferSet } = this.props.offerSuiteStore;
        // if sms enabled and requested then sms fee, or 0
        const smsCharge = selectedOfferSet[PLAN_TERM_MONTHLY].SMSCharge && selectedOfferSet[PLAN_TERM_MONTHLY].SMSPerNumberCharge ? selectedOfferSet[PLAN_TERM_MONTHLY].SMSPerNumberCharge : 0;
        // if taxes are calculated do the math off order total otherwise off offer price
        const total = calculatedTotal ? orderTotal[PLAN_TERM_MONTHLY].TotalEstimatedBeforeDiscount : (selectedOfferSet[PLAN_TERM_MONTHLY].monthlyPlanCost + smsCharge);
        // then multiply it by 12
        return total * 12;
    }

    displayTaxes() {
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;
        const { isTermAnnual, selectedPlanTerm } = this.props.offerSuiteStore;
        // if taxes are calculated
        if (calculatedTotal) {
            const total = orderTotal[selectedPlanTerm].TaxesAndSurcharges;
            // if the term is annual, multiply by 12
            return formatCurrency(isTermAnnual ? total / 12 : total);
        }

        return '–';
    }

    // render savings copy
    AnnualSwitchDetail() {
        const { isUserConvertingToPaid } = this.props.convertToPaidStore;
        const { calculatedTotal, orderTotal } = isUserConvertingToPaid ? this.props.convertToPaidStore : this.props.billingInfoStore;
        const { isTermMonthly } = this.props.offerSuiteStore;

        if (calculatedTotal) {
            const costDiff = (orderTotal[PLAN_TERM_MONTHLY].TotalEstimatedBeforeDiscount * 12) - orderTotal[PLAN_TERM_ANNUAL].TotalEstimatedBeforeDiscount;
            const total = formatCurrency(costDiff <= 0 ? 0 : costDiff);
            return isTermMonthly ? <span>Save <strong>{total}</strong> with annual billing!</span> : <span>You're saving <strong>{total}</strong>!</span>;
        }

        return isTermMonthly ?
            <span>Save <strong>over {BASE_ANNUAL_DISCOUNT_PERCENTAGE}</strong> with annual billing!</span>
            :
            <span>You're saving <strong>over {BASE_ANNUAL_DISCOUNT_PERCENTAGE}</strong>!</span>;
    }


    renderBillingCycle() {
        const { isTermMonthly } = this.props.offerSuiteStore;

        return (
            <section className={styles.section}>
                <div className={`${styles.row} ${styles['row--align-start']}`}>
                    <h4 className={styles.detail}>
                        Billing Cycle:
                    </h4>
                    <span className={styles.amount}>
                        <AnnualPricingSwitch
                            handleCheckboxPress={() => {
                                this.props.offerSuiteStore.setSelectedPlanTerm();
                                gtm(createAnnualPricingEvent());
                            }}
                            isDetailLeft={false}
                            isSwitchLeft={isTermMonthly}
                            leftText={'Monthly'}
                            name={'toggle-switch__checkbox'}
                            rightText={'Annually'}
                            size={'compact'}
                        >{this.AnnualSwitchDetail()}</AnnualPricingSwitch>
                    </span>
                </div>
            </section>
        );
    }

    renderBillingDetails() {
        const { convertToPaidStore, lockNumberStore, transferNumberStore, offerSuiteStore, loginStore } = this.props;
        const { selectedNumberUI } = lockNumberStore;
        const { transferNumber, transferNumberValue } = transferNumberStore;
        const { offerName, offerRecurringCharge, isTermAnnual } = offerSuiteStore;
        const { isUserConvertingToPaid } = convertToPaidStore;
        const convertingNumber = isUserConvertingToPaid ?
            loginStore.userNumber
            :
            selectedNumberUI;

        const isTransferNeeded = isUserConvertingToPaid ? convertToPaidStore.isTransferNeeded : transferNumber;

        return (
            <section className={styles.section}>
                {/* selected number */}
                {!!convertingNumber &&
                    <div className={styles.row}>
                        <h4 className={styles.detail}>
                            {convertingNumber}
                        </h4>
                        <span className={styles.amount}>
                            {/* if the number is temporary let the user know */}
                            {isTransferNeeded ? 'TEMPORARY' : 'FREE'}
                        </span>
                    </div>
                }
                {/* if user is requesting a transfer, display the number to be transferred */}
                {isTransferNeeded &&
                    <div className={styles.row}>
                        <h4 className={`${
                            styles.detail} ${
                            isUserConvertingToPaid ? styles['detail--emphasis'] : ''
                        }`}
                        >
                            {isUserConvertingToPaid ? '[Requested Number Port]' : transferNumberValue}
                        </h4>
                        <span className={styles.amount}>
                            {isUserConvertingToPaid ? 'PENDING' : 'REQUESTED'}
                        </span>
                    </div>
                }
                {/* display selected offer details */}
                {!!offerName &&
                    <div className={`${styles.row} ${styles['row--margin-top']}`}>
                        <h4 className={styles.detail}>
                            {offerName}
                        </h4>
                        <span className={styles.amount}>
                            {isTermAnnual ? <span className={styles.subcopy}>12 x </span> : null}
                            {formatCurrency(offerRecurringCharge)}
                        </span>
                    </div>
                }
                {/* display taxes information if calculated */}
                <div className={`${styles.row} ${styles['row--margin-top']}`}>
                    <h4 className={styles.detail}>
                        <Tooltip
                            activeTriggerModifiers={styles['tooltip-trigger--active']}
                            content={
                                <div>
                                    <p>
                                        State and Federal taxes, Federal Universal Service Fee{offerName === 'True Solo' ? '.' : ', and Grasshopper Cost Recovery Fee.'}
                                    </p>
                                    <p>
                                        Estimation only–subject to monthly usage.
                                    </p>
                                    <p>
                                        More information available via billing statement.
                                    </p>
                                </div>
                            }
                            triggerModifiers={styles['tooltip-trigger']}
                        >
                            Taxes &amp; Surcharges
                            <InfoIcon
                                // blue={true}
                                modifier={`${
                                    styles['info-icon']} ${
                                    IS_TOUCH_DEVICE ? styles['info-icon--touch-device'] : ''
                                }`}
                            />
                        </Tooltip>
                    </h4>
                    <span className={styles.amount} id="billing__taxes">
                        {isTermAnnual ? <span className={styles.subcopy}>12 x </span> : null}
                        {this.displayTaxes()}
                    </span>
                </div>
            </section>
        );
    }

    renderTotal() {
        const { promoCodeAmount, isCouponValid, discounts } = this.props.promoCodeStore;
        const { isTermMonthly, selectedOfferSet, selectedPlanTerm } = this.props.offerSuiteStore;
        const { isCheckoutABTest } = this.props;

        return (
            <section className={styles.section}>
                {/* if coupon display subtotal*/}
                {isCouponValid &&
                    <div className={styles.row}>
                        <h4 className={`${styles.detail} ${styles['detail--subtotal']}`}>
                            Total
                        </h4>
                        <span className={`${styles.amount} ${styles['amount--total']}`}>
                            {formatCurrency(this.calculateSubtotal())}{!isCheckoutABTest && (`/${isTermMonthly ? 'mo' : 'year'}`)}
                        </span>
                    </div>
                }
                {/* if coupon display coupon amount */}
                {isCouponValid && !!promoCodeAmount &&
                    <div className={`${styles.row} ${styles['row--discount']} ${styles['row--margin-top']}`} data-test="order-summary-discount">
                        <h4 className={styles.detail}>
                            Discount
                        </h4>
                        <span className={`${styles.amount} ${styles['amount--discount']}`} id="billing__taxes">
                            - {formatCurrency(promoCodeAmount)}
                            <span className={styles['sub-amount']}>
                               You're saving an additional <b>{discounts[selectedOfferSet[selectedPlanTerm].Id]?.discountPercent}%</b>
                            </span>
                        </span>
                    </div>
                }
                {/* display display total or subtotal minus coupon amount */}
                <div className={`${styles.row} ${isCouponValid ? styles['row--margin-top'] : ''}`}>
                    <h4 className={`${styles.detail} ${styles['detail--total']}`}>
                        Due Now
                    </h4>
                    <span className={`${styles.amount} ${styles['amount--total']}`}>
                        {formatCurrency(this.calculatedTotal())}
                        {(!isCouponValid || isCheckoutABTest) ? isTermMonthly ? '/mo' : '/year' : ''}
                    </span>
                </div>
            </section>
        );
    }

    render() {
        const { hasAnnualPlans } = this.props.offerSuiteStore;
        return (
            <div
                className={styles.wrapper}
                data-test="order-summary"
                ref="orderSummary"
            >
                <h3 className={styles.header}>
                    Order Summary
                </h3>
                {hasAnnualPlans ? this.renderBillingCycle() : null}
                {this.renderBillingDetails()}
                {this.renderTotal()}
            </div>
        );
    }
}
